import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  MessageCircle,
  Send,
  X,
  Bot,
  User,
  Sparkles,
  ChevronRight,
  RefreshCw,
  Lightbulb,
  HelpCircle
} from 'lucide-react';

/**
 * AI Compliance Chatbot
 * Interactive assistant for ESG compliance questions
 */
const ComplianceChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      fetchSuggestions();
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: "Hello! I'm your ESG Compliance Assistant. I can help you with questions about CSRD, ESRS, GRI, TCFD, Scope 3 emissions, and more. What would you like to know?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSuggestions = async () => {
    const data = await apiFetch(`${API}/chatbot/suggestions`);
    if (data?._error) {
      setSuggestions([
        "What is CSRD?",
        "How do I calculate Scope 3 emissions?",
        "What is double materiality?"
      ]);
      return;
    }
    setSuggestions(data.suggestions || []);
  };

  const handleSend = async (message = input) => {
    if (!message.trim()) return;

    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    const data = await apiFetch(`${API}/chatbot/message`, {
      method: 'POST',
      body: {
        message: message,
        session_id: sessionId
      }
    });

    if (data?._error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } else {
      const assistantMessage = {
        role: 'assistant',
        content: data.answer || "I couldn't process that question. Please try again.",
        timestamp: new Date().toISOString(),
        followUps: data.follow_up_questions || [],
        confidence: data.confidence
      };

      setMessages(prev => [...prev, assistantMessage]);
    }
    setLoading(false);
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50" data-testid="compliance-chatbot">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Bot size={22} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">ESG Compliance Assistant</h3>
            <p className="text-emerald-100 text-xs">Powered by AI</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
              <div
                className={`p-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : msg.error
                    ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
              
              {/* Follow-up questions */}
              {msg.followUps && msg.followUps.length > 0 && (
                <div className="mt-2 space-y-1">
                  {msg.followUps.map((q, qIdx) => (
                    <button
                      key={qIdx}
                      onClick={() => handleSend(q)}
                      className="w-full text-left text-xs p-2 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-emerald-700 flex items-center gap-1 transition-colors"
                    >
                      <ChevronRight size={12} />
                      {q}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Confidence indicator */}
              {msg.confidence && (
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Sparkles size={10} />
                  {msg.confidence} confidence
                </p>
              )}
            </div>
          </div>
        ))}
        
        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl rounded-bl-md p-3">
              <div className="flex items-center gap-2">
                <RefreshCw size={14} className="animate-spin text-emerald-600" />
                <span className="text-sm text-slate-600">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions (shown when no messages yet) */}
      {messages.length <= 1 && suggestions.length > 0 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Lightbulb size={12} />
            Suggested questions:
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.slice(0, 4).map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestionClick(suggestion)}
                className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-emerald-100 rounded-full text-slate-700 hover:text-emerald-700 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about ESG compliance..."
            className="flex-1 px-4 py-2 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            disabled={loading}
            data-testid="chatbot-input"
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || loading}
            className="p-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="chatbot-send"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Floating Chatbot Button
 */
export const ChatbotButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-4 right-4 w-14 h-14 bg-emerald-600 text-white rounded-full shadow-lg hover:bg-emerald-700 transition-all hover:scale-105 flex items-center justify-center z-40"
    data-testid="chatbot-button"
  >
    <MessageCircle size={24} />
  </button>
);

export default ComplianceChatbot;
